import React from 'react';
import { FaShieldAlt, FaClock, FaUsers, FaChartLine } from 'react-icons/fa';
import Layout from '../components/Layout';
import SEO from '../components/SEO';

const PrivacyPolicy: React.FC = () => {
  const sections = [
    {
      icon: FaShieldAlt,
      title: 'Collecte des Données',
      content: 'Nous collectons les informations que vous nous fournissez directement lorsque vous utilisez notre site, notamment lors de l\'inscription à notre newsletter ou de l\'utilisation de nos services de comparaison.',
    },
    {
      icon: FaChartLine,
      title: 'Utilisation des Données',
      content: 'Les données collectées sont utilisées pour améliorer votre expérience utilisateur, personnaliser le contenu, analyser le trafic du site et vous envoyer des communications pertinentes concernant les paris sportifs.',
    },
    {
      icon: FaClock,
      title: 'Protection des Données',
      content: 'Nous mettons en œuvre des mesures de sécurité techniques et organisationnelles appropriées pour protéger vos données personnelles contre tout accès non autorisé, perte ou destruction.',
    },
    {
      icon: FaUsers,
      title: 'Vos Droits',
      content: 'Conformément au RGPD, vous disposez d\'un droit d\'accès, de rectification, de suppression et de portabilité de vos données personnelles. Vous pouvez également vous opposer au traitement de vos données ou demander leur limitation.',
    },
  ];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Politique de Confidentialité - Parismin',
    description: 'Politique de confidentialité de Parismin concernant la collecte, l\'utilisation et la protection de vos données personnelles.',
  };

  return (
    <Layout>
      <SEO
        title="Politique de Confidentialité - Parismin"
        description="Découvrez comment Parismin collecte, utilise et protège vos données personnelles conformément au RGPD."
        url="/privacy"
        jsonLd={jsonLd}
      />

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-purple-600/10 to-pink-600/10"></div>
        <div className="relative z-10 container mx-auto px-4 text-center">
          <div className="inline-block mb-6 px-4 py-2 glass-card rounded-full text-sm font-semibold border border-cyan-500/30">
            <FaShieldAlt className="inline mr-2 text-cyan-400" />
            Confidentialité
          </div>
          <h1 className="text-5xl md:text-6xl font-black mb-6 heading-font">
            <span className="text-gradient">Politique de Confidentialité</span>
          </h1>
          <p className="text-gray-400 text-lg max-w-3xl mx-auto">
            Dernière mise à jour : {new Date().toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-12 pb-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Introduction */}
            <div className="glass-card rounded-3xl p-8 md:p-12 mb-12">
              <h2 className="text-3xl font-black mb-6 heading-font text-gradient">Introduction</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                Parismin s'engage à protéger votre vie privée et vos données personnelles. Cette politique de confidentialité explique comment nous collectons, utilisons, stockons et protégeons vos informations lorsque vous utilisez notre site web.
              </p>
              <p className="text-gray-300 leading-relaxed">
                En utilisant notre site, vous acceptez les pratiques décrites dans cette politique. Si vous n'acceptez pas ces pratiques, veuillez ne pas utiliser notre site.
              </p>
            </div>

            {/* Main Sections */}
            <div className="space-y-8 mb-12">
              {sections.map((section, index) => {
                const Icon = section.icon;
                return (
                  <div key={index} className="glass-card rounded-3xl p-8">
                    <div className="flex items-start space-x-4">
                      <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 via-purple-500 to-pink-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                        <Icon className="text-white text-2xl" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold mb-4 heading-font">{section.title}</h3>
                        <p className="text-gray-300 leading-relaxed">{section.content}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Additional Information */}
            <div className="glass-card rounded-3xl p-8 md:p-12">
              <h2 className="text-3xl font-black mb-6 heading-font text-gradient">Informations Complémentaires</h2>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-bold mb-3 heading-font">Cookies et Technologies Similaires</h3>
                  <p className="text-gray-300 leading-relaxed">
                    Nous utilisons des cookies et des technologies similaires pour améliorer votre expérience. Pour plus d'informations, consultez notre{' '}
                    <a href="/cookies" className="text-cyan-400 hover:text-cyan-300 underline">Politique des Cookies</a>.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-bold mb-3 heading-font">Partage des Données</h3>
                  <p className="text-gray-300 leading-relaxed">
                    Nous ne vendons pas vos données personnelles à des tiers. Nous pouvons partager certaines informations avec nos partenaires de confiance uniquement dans le cadre de l'amélioration de nos services.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-bold mb-3 heading-font">Conservation des Données</h3>
                  <p className="text-gray-300 leading-relaxed">
                    Nous conservons vos données personnelles aussi longtemps que nécessaire pour fournir nos services et respecter nos obligations légales. Vous pouvez demander la suppression de vos données à tout moment.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-bold mb-3 heading-font">Contact</h3>
                  <p className="text-gray-300 leading-relaxed">
                    Pour toute question concernant cette politique de confidentialité ou pour exercer vos droits, veuillez nous contacter à{' '}
                    <a href="mailto:contact@parismin.com" className="text-cyan-400 hover:text-cyan-300 underline">contact@parismin.com</a>.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default PrivacyPolicy;

